import type { AllianceSunView, RoundStatus, SunAttackAlert, SunControl, SunControlView, SunStatus } from '@kaeliux/shared';
import { toDate } from '@kaeliux/shared';

/**
 * Wire → `Date` normalization for every Sun-control and round payload. Shared
 * by the real services and their mocks (which round-trip through
 * localStorage as JSON), so neither ever hands a component an ISO string
 * where the type promises a `Date`.
 */

type Loose = Record<string, unknown>;

function optionalDate(value: unknown): Date | undefined {
    return value === undefined || value === null ? undefined : toDate(value);
}

function withDates<T extends Loose>(raw: T, keys: readonly string[]): T {
    const out: Loose = { ...raw };
    for (const key of keys) {
        const value = optionalDate(raw[key]);
        if (value) out[key] = value;
        else delete out[key];
    }
    return out as T;
}

const CONTROL_DATES = ['claimedAt', 'claimExpiresAt', 'controlledAt', 'reclaimImmuneUntil', 'topUpExpiresAt'] as const;

export function normalizeSunControl(raw: unknown): SunControl {
    return withDates(raw as Loose, CONTROL_DATES) as unknown as SunControl;
}

export function normalizeSunControlView(raw: unknown): SunControlView {
    return withDates(raw as Loose, CONTROL_DATES) as unknown as SunControlView;
}

export function normalizeSunStatus(raw: unknown): SunStatus {
    const data = withDates(raw as Loose, ['nextAttackEligibleAt']);
    return {
        ...data,
        ...(data.defenders ? { defenders: (data.defenders as Loose[]).map((d) => ({ ...d, arrivedAt: toDate(d.arrivedAt) })) } : {}),
        ...(data.control ? { control: normalizeSunControlView(data.control) } : {}),
    } as unknown as SunStatus;
}

export function normalizeSunAttackAlert(raw: unknown): SunAttackAlert {
    return withDates(raw as Loose, ['arrivesAt']) as unknown as SunAttackAlert;
}

export function normalizeAllianceSun(raw: unknown): AllianceSunView {
    const data = withDates(raw as Loose, ['attackArrivesAt', 'nextAttackEligibleAt']);
    return { ...data, control: normalizeSunControl(data.control) } as unknown as AllianceSunView;
}

export function normalizeRoundStatus(raw: unknown): RoundStatus {
    const data = withDates(raw as Loose, ['roundStartedAt', 'endgameStartedAt', 'endgameEndsAt', 'deathMatchEndsAt']);
    return {
        ...data,
        ...(data.victoryContest ? { victoryContest: withDates(data.victoryContest as Loose, ['startedAt', 'endsAt']) } : {}),
        ...(data.winner ? { winner: withDates(data.winner as Loose, ['decidedAt']) } : {}),
    } as unknown as RoundStatus;
}
